import React, { useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { DuplicadoCandidato, buscarCandidatos, interpretarErrorDuplicados } from '../../../api/clientesDuplicadosApi';

interface BuscadorClienteProps {
  etiqueta: string;
  valor: DuplicadoCandidato | null;
  /** El cliente ya elegido en la otra columna: no se ofrece dos veces (§4.1). */
  excluir?: number;
  disabled?: boolean;
  onElegir: (c: DuplicadoCandidato | null) => void;
}

/** Los mismos caracteres que rechaza `DuplicadoBusquedaQueryValidator`: no vale la pena ir al servidor. */
const CARACTERES_NO_ADMITIDOS = /[%_[\]]/;

/**
 * Buscador por nombre o código del paso 1 (§4.1). Búsqueda pobre a propósito: la detección de
 * duplicados no es de esta herramienta — el operador ya sabe qué dos fichas quiere enfrentar.
 */
export const BuscadorCliente: React.FC<BuscadorClienteProps> = ({ etiqueta, valor, excluir, disabled, onElegir }) => {
  const [opciones, setOpciones] = useState<DuplicadoCandidato[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState<string | null>('Escribe al menos 3 caracteres del nombre o el código.');
  const temporizador = useRef<number | null>(null);
  const ultimaBusqueda = useRef(0);

  useEffect(() => () => { if (temporizador.current) window.clearTimeout(temporizador.current); }, []);

  const buscar = (texto: string) => {
    const t = texto.trim();
    if (temporizador.current) window.clearTimeout(temporizador.current);
    if (t.length < 3) {
      setOpciones([]);
      setAviso('Escribe al menos 3 caracteres del nombre o el código.');
      return;
    }
    if (CARACTERES_NO_ADMITIDOS.test(t)) {
      setOpciones([]);
      setAviso('La búsqueda no admite los caracteres % _ [ ]. Escribe el nombre o el código tal cual.');
      return;
    }
    temporizador.current = window.setTimeout(async () => {
      const marca = ++ultimaBusqueda.current;
      // Fuera los resultados de la búsqueda anterior: así se ve el spinner, y no se elige uno viejo.
      setOpciones([]);
      setBuscando(true);
      try {
        const res = await buscarCandidatos(t, excluir);
        if (marca !== ultimaBusqueda.current) return;
        setOpciones(res);
        setAviso(res.length === 0 ? 'Sin resultados.' : null);
      } catch (e) {
        if (marca !== ultimaBusqueda.current) return;
        setOpciones([]);
        setAviso(interpretarErrorDuplicados(e).mensaje);
      } finally {
        if (marca === ultimaBusqueda.current) setBuscando(false);
      }
    }, 350);
  };

  return (
    <Select<number>
      showSearch
      allowClear
      disabled={disabled}
      placeholder={etiqueta}
      style={{ width: '100%' }}
      value={valor?.idCliente}
      filterOption={false}
      onSearch={buscar}
      onChange={(id) => onElegir(opciones.find((o) => o.idCliente === id) ?? null)}
      onClear={() => onElegir(null)}
      loading={buscando}
      notFoundContent={buscando ? <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div> : aviso}
      /* Solo código y nombre (Dev, 2026-09-24): el operador ya sabe qué cliente busca y solo quiere
         elegirlo. NIF, provincia, alta, CU2 y farmacia se ven después en la rejilla. */
      options={opciones.map((o) => ({
        value: o.idCliente,
        label: `${o.codigo ?? `ID ${o.idCliente}`} ${o.nombreCompleto ?? ''}`.trim(),
      }))}
    />
  );
};
